// src/components/admin/BackupPanel.jsx
// ── Backup Monitoring Panel — ISO 27001 ───────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = axios.create({ baseURL: "/sysadmin/backup", withCredentials: true });

// Helper to get CSRF token from cookie (required for POST requests)
const getCsrfToken = () => {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

// ── Inline SVG Icons ─────────────────────────────────────────────────────────
const Icon = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  database: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  play: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <triangle points="10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  hdd: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
      <line x1="22" y1="12" x2="2" y2="12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
      <line x1="6" y1="16" x2="6.01" y2="16"/><line x1="10" y1="16" x2="10.01" y2="16"/>
    </svg>
  ),
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const statusColor = (s) => ({
  ok: "#22c55e", compliant: "#22c55e",
  warning: "#f59e0b",
  critical: "#ef4444", non_compliant: "#ef4444",
  unknown: "#6b7280",
}[s] || "#6b7280");

const statusBg = (s) => ({
  ok: "rgba(34,197,94,0.1)", compliant: "rgba(34,197,94,0.1)",
  warning: "rgba(245,158,11,0.1)",
  critical: "rgba(239,68,68,0.1)", non_compliant: "rgba(239,68,68,0.1)",
  unknown: "rgba(107,114,128,0.1)",
}[s] || "rgba(107,114,128,0.1)");

const statusLabel = (s) => ({
  ok: "OK", compliant: "COMPLIANT",
  warning: "WARNING",
  critical: "CRITICAL", non_compliant: "NON-COMPLIANT",
  unknown: "UNKNOWN",
}[s] || s?.toUpperCase());

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
};

const fmtSize = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`;

// ── Status Badge Component ────────────────────────────────────────────────────
const StatusBadge = ({ status, large }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "5px",
    padding: large ? "6px 14px" : "3px 10px",
    borderRadius: "20px",
    background: statusBg(status),
    color: statusColor(status),
    fontSize: large ? "13px" : "11px",
    fontWeight: "700",
    letterSpacing: "0.05em",
    border: `1px solid ${statusColor(status)}40`,
  }}>
    <span style={{
      width: large ? "8px" : "6px", height: large ? "8px" : "6px",
      borderRadius: "50%", background: statusColor(status),
      boxShadow: `0 0 6px ${statusColor(status)}`,
    }}/>
    {statusLabel(status)}
  </span>
);

// ── Compliance Card Component ─────────────────────────────────────────────────
const ComplianceCard = ({ label, data, unit = "h" }) => {
  const ago = unit === "h" ? data?.hours_ago : data?.days_ago;
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: `1px solid ${statusColor(data?.status)}30`,
      borderLeft: `3px solid ${statusColor(data?.status)}`,
      borderRadius: "8px",
      padding: "14px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {label}
        </span>
        <StatusBadge status={data?.status} />
      </div>
      <div style={{ color: "#e2e8f0", fontSize: "13px" }}>
        {data?.last_backup ? (
          <>
            <div style={{ marginBottom: "3px" }}>{fmtDate(data.last_backup)}</div>
            <div style={{ color: "#64748b", fontSize: "12px" }}>
              {ago} {unit === "h" ? "hour(s)" : "day(s)"} ago
            </div>
          </>
        ) : (
          <div style={{ color: "#ef4444" }}>No backup found</div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
export default function BackupPanel() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        API.get("/status"),
        API.get("/history?limit=20"),
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data.history || []);
      setError(null);
    } catch (e) {
      setError("Unable to load backup data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // ── FIXED: Manual backup trigger with CSRF token ───────────────────────────
  const triggerBackup = async () => {
    if (!window.confirm("Trigger manual backup now?")) return;
    setTriggering(true);
    try {
      const csrfToken = getCsrfToken();
      await API.post("/trigger", {}, {
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {}
      });
      setTimeout(fetchStatus, 5000);
    } catch (e) {
      alert("Error triggering backup");
    } finally {
      setTriggering(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const styles = {
    container: {
      background: "#0f172a",
      border: "1px solid #1e293b",
      borderRadius: "12px",
      overflow: "hidden",
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    },
    header: {
      padding: "20px 24px",
      borderBottom: "1px solid #1e293b",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "rgba(15,23,42,0.8)",
    },
    title: {
      display: "flex", alignItems: "center", gap: "10px",
      color: "#e2e8f0", fontSize: "16px", fontWeight: "700",
    },
    tabs: {
      display: "flex", gap: "2px",
      padding: "12px 24px",
      borderBottom: "1px solid #1e293b",
      background: "rgba(15,23,42,0.5)",
    },
    tab: (active) => ({
      padding: "7px 16px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: active ? "600" : "400",
      background: active ? "rgba(59,130,246,0.15)" : "transparent",
      color: active ? "#60a5fa" : "#64748b",
      transition: "all 0.15s",
    }),
    body: { padding: "20px 24px" },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" },
    grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" },
    card: {
      background: "rgba(255,255,255,0.03)",
      border: "1px solid #1e293b",
      borderRadius: "8px",
      padding: "16px",
    },
    cardLabel: { color: "#64748b", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" },
    cardValue: { color: "#e2e8f0", fontSize: "22px", fontWeight: "700" },
    cardSub: { color: "#64748b", fontSize: "12px", marginTop: "3px" },
    sectionTitle: { color: "#94a3b8", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "12px", marginTop: "20px" },
    btn: (variant) => ({
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "8px 16px",
      borderRadius: "7px",
      border: "none",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      transition: "all 0.15s",
      ...(variant === "primary" ? {
        background: "rgba(59,130,246,0.15)",
        color: "#60a5fa",
        border: "1px solid rgba(59,130,246,0.3)",
      } : {
        background: "rgba(255,255,255,0.05)",
        color: "#94a3b8",
        border: "1px solid #1e293b",
      })
    }),
    historyRow: {
      display: "grid",
      gridTemplateColumns: "80px 90px 120px 80px 1fr",
      gap: "12px",
      padding: "10px 0",
      borderBottom: "1px solid #0f172a",
      fontSize: "13px",
      alignItems: "center",
    },
    encBadge: {
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "2px 8px",
      borderRadius: "4px",
      background: "rgba(139,92,246,0.15)",
      color: "#a78bfa",
      fontSize: "11px",
      fontWeight: "600",
      border: "1px solid rgba(139,92,246,0.2)",
    },
  };

  if (loading) return (
    <div style={{ ...styles.container, padding: "40px", textAlign: "center", color: "#64748b" }}>
      Loading backup data...
    </div>
  );

  if (error) return (
    <div style={{ ...styles.container, padding: "24px", color: "#ef4444", textAlign: "center" }}>
      {error}
    </div>
  );

  const { compliance, storage, disk, stats, encryption, policy } = status || {};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          {Icon.shield}
          Backup & ISO 27001 Compliance
          <StatusBadge status={status?.overall_status} large />
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={styles.btn("ghost")} onClick={fetchStatus}>
            {Icon.refresh} Refresh
          </button>
          <button
            style={styles.btn("primary")}
            onClick={triggerBackup}
            disabled={triggering}
          >
            {Icon.play} {triggering ? "In progress..." : "Manual backup"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {["overview", "history", "storage", "policy"].map(tab => (
          <button key={tab} style={styles.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
            {{ overview: "Overview", history: "History", storage: "Storage", policy: "Policy" }[tab]}
          </button>
        ))}
      </div>

      <div style={styles.body}>

        {/* ── TAB: Overview ────────────────────────────────── */}
        {activeTab === "overview" && (
          <>
            {/* Global stats */}
            <div style={styles.grid3}>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Success rate</div>
                <div style={{ ...styles.cardValue, color: stats?.success_rate > 95 ? "#22c55e" : "#f59e0b" }}>
                  {stats?.success_rate}%
                </div>
                <div style={styles.cardSub}>{stats?.total_success} succeeded / {stats?.total_failed} failed</div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Encryption</div>
                <div style={{ ...styles.cardValue, fontSize: "14px", color: "#a78bfa", marginTop: "4px" }}>
                  {encryption?.algorithm}
                </div>
                <div style={styles.cardSub}>{encryption?.key_derivation} · {encryption?.iterations?.toLocaleString()} iterations</div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardLabel}>Disk space</div>
                <div style={{ ...styles.cardValue, color: statusColor(disk?.status) }}>
                  {disk?.available_mb ? `${disk.available_mb} MB` : "—"}
                </div>
                <div style={styles.cardSub}>{disk?.used_percent}% used · {disk?.status?.toUpperCase()}</div>
              </div>
            </div>

            {/* Compliance by frequency */}
            <div style={styles.sectionTitle}>Compliance by frequency</div>
            <div style={styles.grid3}>
              <ComplianceCard label="Daily" data={compliance?.daily} unit="h" />
              <ComplianceCard label="Weekly" data={compliance?.weekly} unit="d" />
              <ComplianceCard label="Monthly" data={compliance?.monthly} unit="d" />
            </div>

            {/* 3-2-1 Rule */}
            <div style={styles.sectionTitle}>3-2-1 Rule (ISO 27001 A.12.3)</div>
            <div style={{
              background: "rgba(59,130,246,0.05)",
              border: "1px solid rgba(59,130,246,0.15)",
              borderRadius: "8px",
              padding: "16px",
            }}>
              {[
                { label: "3 copies of data", ok: true },
                { label: "2 different media (daily + weekly/monthly)", ok: true },
                { label: "1 off-site copy (recommended: S3/Backblaze)", ok: false, warn: true },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 0", color: item.ok ? "#22c55e" : item.warn ? "#f59e0b" : "#ef4444", fontSize: "13px" }}>
                  <span style={{ opacity: 0.9 }}>{item.ok ? "✅" : item.warn ? "⚠️" : "❌"}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── TAB: History ───────────────────────────────────── */}
        {activeTab === "history" && (
          <>
            <div style={{ ...styles.historyRow, color: "#64748b", fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid #1e293b" }}>
              <span>Type</span><span>Status</span><span>Date</span><span>Size</span><span>Checksum</span>
            </div>
            {history.length === 0 ? (
              <div style={{ color: "#64748b", padding: "20px 0", textAlign: "center" }}>No history available</div>
            ) : history.map((entry, i) => (
              <div key={i} style={styles.historyRow}>
                <span style={{
                  display: "inline-block", padding: "2px 8px", borderRadius: "4px",
                  background: { daily: "rgba(59,130,246,0.1)", weekly: "rgba(139,92,246,0.1)", monthly: "rgba(245,158,11,0.1)" }[entry.type] || "rgba(255,255,255,0.05)",
                  color: { daily: "#60a5fa", weekly: "#a78bfa", monthly: "#fbbf24" }[entry.type] || "#94a3b8",
                  fontSize: "11px", fontWeight: "600",
                }}>
                  {entry.type?.toUpperCase()}
                </span>
                <StatusBadge status={entry.status === "success" ? "ok" : "critical"} />
                <span style={{ color: "#94a3b8", fontSize: "12px" }}>{fmtDate(entry.timestamp)}</span>
                <span style={{ color: "#e2e8f0" }}>{entry.size_mb ? fmtSize(entry.size_mb) : "—"}</span>
                <span style={{ color: "#475569", fontSize: "11px", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {entry.checksum ? entry.checksum.slice(0, 16) + "..." : "—"}
                </span>
              </div>
            ))}
          </>
        )}

        {/* ── TAB: Storage ─────────────────────────────────────── */}
        {activeTab === "storage" && (
          <>
            {["daily", "weekly", "monthly"].map(type => {
              const s = storage?.[type];
              return (
                <div key={type} style={{ ...styles.card, marginBottom: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {Icon.hdd}
                      <div>
                        <div style={{ color: "#e2e8f0", fontWeight: "600", fontSize: "14px", textTransform: "capitalize" }}>
                          {type} backups
                        </div>
                        <div style={{ color: "#64748b", fontSize: "12px", marginTop: "2px" }}>
                          Retention: {policy?.[`${type}_retention_days`]} days
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#e2e8f0", fontWeight: "700", fontSize: "18px" }}>
                        {s?.count || 0} files
                      </div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        {s?.total_size_mb ? fmtSize(s.total_size_mb) : "0 MB"}
                      </div>
                    </div>
                  </div>
                  {s?.newest && (
                    <div style={{ marginTop: "10px", padding: "8px 12px", background: "rgba(255,255,255,0.02)", borderRadius: "6px", fontSize: "12px", color: "#64748b" }}>
                      Latest: <span style={{ color: "#94a3b8" }}>{fmtDate(s.newest)}</span>
                      &nbsp;·&nbsp;
                      Oldest: <span style={{ color: "#94a3b8" }}>{fmtDate(s.oldest)}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Encryption badges */}
            <div style={{ marginTop: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <span style={styles.encBadge}>{Icon.lock} AES-256-CBC</span>
              <span style={styles.encBadge}>PBKDF2 · 100k iterations</span>
              <span style={styles.encBadge}>SHA-256 checksum</span>
              <span style={styles.encBadge}>Format: pg_dump -Fc</span>
            </div>
          </>
        )}

        {/* ── TAB: Policy ────────────────────────────────────── */}
        {activeTab === "policy" && (
          <div>
            {[
              { title: "A.12.3 — Information Backup", status: "ok", items: [
                "Automated daily backups (cron 02:00 UTC)",
                "Weekly backups every Monday",
                "Monthly backups on the 1st",
                "AES-256-CBC encryption with PBKDF2",
                "SHA-256 integrity check on every backup",
              ]},
              { title: "A.12.3.1 — Retention", status: "ok", items: [
                `Daily: ${policy?.daily_retention_days} days retention`,
                `Weekly: ${policy?.weekly_retention_days} days retention`,
                `Monthly: ${policy?.monthly_retention_days} days retention`,
                "Automatic cleanup of old files",
              ]},
              { title: "A.17.1 — Security Continuity", status: "warning", items: [
                "Backup on main VPS ✅",
                "3-2-1 rule partially respected ⚠️",
                "Off-site copy (S3/Backblaze) recommended",
              ]},
            ].map((section, i) => (
              <div key={i} style={{ ...styles.card, marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <div style={{ color: "#e2e8f0", fontWeight: "600", fontSize: "14px" }}>{section.title}</div>
                  <StatusBadge status={section.status} />
                </div>
                {section.items.map((item, j) => (
                  <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", padding: "5px 0", color: "#94a3b8", fontSize: "13px" }}>
                    <span style={{ color: "#22c55e", marginTop: "1px", flexShrink: 0 }}>•</span>
                    {item}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}